// src/components/timeBoxes/TimeBoxDetail/TimeBoxDetailMetadata.tsx
// TimeBox 詳情元數據區：統一渲染桌面和行動版

import React, { useState } from 'react';
import { TimeBox } from '../../../../types/timeBox';
import { Clock, MapPin, CalendarClock } from '../../../../components/common/icons';
import MetadataRow from '../../../../components/tasks/MetadataRow';
import SimpleDatePicker from '../../../../components/common/SimpleDatePicker';
import TimePickerModal from '../../../../components/common/TimePickerModal';
import { combineDateTime, deconstructDateTime, calculateDuration } from '../../../../utils/timeBoxHelpers';
import { formatDurationFromMinutes } from '../../../../utils/formatters';

interface TimeBoxDetailMetadataProps {
  timeBox: TimeBox;
  onStartTimeChange: (startTime: Date) => Promise<void>;
  onEndTimeChange: (endTime: Date) => Promise<void>;
  onLocationChange: (location: string | undefined) => Promise<void>;
  variant: 'desktop' | 'mobile';
}

interface ModalStates {
  startDatePicker: boolean;
  startTimePicker: boolean;
  endDatePicker: boolean;
  endTimePicker: boolean;
}

export const TimeBoxDetailMetadata: React.FC<TimeBoxDetailMetadataProps> = ({
  timeBox,
  onStartTimeChange,
  onEndTimeChange,
  onLocationChange,
  variant
}) => {
  const isMobile = variant === 'mobile';
  const showLabelOnMobile = isMobile;

  // Modal 狀態管理
  const [modals, setModals] = useState<ModalStates>({
    startDatePicker: false,
    startTimePicker: false,
    endDatePicker: false,
    endTimePicker: false,
  });

  // 暫存選中的日期（用於分步驟流程）
  const [tempStartDate, setTempStartDate] = useState<string>('');
  const [tempEndDate, setTempEndDate] = useState<string>('');

  // 解構時間
  const { dateStr: startDateStr, timeStr: startTimeStr } = deconstructDateTime(timeBox.startTime);
  const { dateStr: endDateStr, timeStr: endTimeStr } = deconstructDateTime(timeBox.endTime);

  // 計算時長
  const duration = formatDurationFromMinutes(
    calculateDuration(timeBox.startTime, timeBox.endTime)
  );

  // TimeBox 專用的日期快捷選項（今天、明天、週末）
  const getTimeBoxQuickDateOptions = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    // 計算本週末（週六）
    const dayOfWeek = today.getDay();
    const daysUntilSaturday = dayOfWeek === 0 ? 6 : (6 - dayOfWeek);
    const thisSaturday = new Date(today);
    thisSaturday.setDate(today.getDate() + daysUntilSaturday);

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return [
      { value: formatDate(today), label: 'Today' },
      { value: formatDate(tomorrow), label: 'Tomorrow' },
      { value: formatDate(thisSaturday), label: 'This weekend' },
    ];
  };

  // Modal 管理輔助函數
  const openModal = (modal: keyof ModalStates) => {
    setModals((prev) => ({ ...prev, [modal]: true }));
  };

  const closeModal = (modal: keyof ModalStates) => {
    setModals((prev) => ({ ...prev, [modal]: false }));
  };

  // 處理開始時間選擇流程
  const handleStartTimeClick = () => {
    openModal('startDatePicker');
  };

  const handleStartDateSelected = (dateStr: string) => {
    setTempStartDate(dateStr);
    closeModal('startDatePicker');
    openModal('startTimePicker');
  };

  const handleStartTimeSelected = async (timeStr: string) => {
    const newStartTime = combineDateTime(tempStartDate, timeStr);
    await onStartTimeChange(newStartTime);
    closeModal('startTimePicker');
    setTempStartDate('');
  };

  const handleStartTimeBack = () => {
    closeModal('startTimePicker');
    openModal('startDatePicker');
  };

  // 處理結束時間選擇流程
  const handleEndTimeClick = () => {
    openModal('endDatePicker');
  };

  const handleEndDateSelected = (dateStr: string) => {
    setTempEndDate(dateStr);
    closeModal('endDatePicker');
    openModal('endTimePicker');
  };

  const handleEndTimeSelected = async (timeStr: string) => {
    const newEndTime = combineDateTime(tempEndDate, timeStr);
    await onEndTimeChange(newEndTime);
    closeModal('endTimePicker');
    setTempEndDate('');
  };

  const handleEndTimeBack = () => {
    closeModal('endTimePicker');
    openModal('endDatePicker');
  };

  // 計算 End Time 的 minTime 限制
  const getEndTimeMinTime = (): string | undefined => {
    // 只有當 End Date 與 Start Date 同天時才限制
    if (tempEndDate !== startDateStr) {
      return undefined;
    }

    // 計算 Start Time + 15 分鐘作為最小時間
    const [startHour, startMinute] = startTimeStr.split(':').map(Number);
    const startTotalMinutes = startHour * 60 + startMinute;
    const minTotalMinutes = startTotalMinutes + 15;

    // 防止跨午夜溢出（23:45 之後）
    if (minTotalMinutes >= 24 * 60) {
      return undefined; // 超過 23:45，不限制（讓用戶選跨日）
    }

    const minHour = Math.floor(minTotalMinutes / 60);
    const minMinute = minTotalMinutes % 60;

    return `${String(minHour).padStart(2, '0')}:${String(minMinute).padStart(2, '0')}`;
  };

  const containerClass = isMobile
    ? 'md:hidden mt-6 px-4'
    : 'hidden md:flex md:w-52 md:shrink-0 md:flex-col';

  const innerClass = isMobile
    ? 'space-y-0'
    : 'metadata-sidebar sticky top-4 flex-1';

  return (
    <div className={containerClass}>
      {/* Mobile 分隔線 */}
      {isMobile && <div className="meta-divider mb-4" />}

      <div className={innerClass}>
        {/* Time Section */}

        {/* Start Time */}
        <MetadataRow
          icon={CalendarClock}
          label="Start"
          value={new Date(timeBox.startTime).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}
          onClick={handleStartTimeClick}
          showLabelOnMobile={showLabelOnMobile}
        />

        {/* End Time */}
        <MetadataRow
          icon={CalendarClock}
          label="End"
          value={new Date(timeBox.endTime).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}
          onClick={handleEndTimeClick}
          showLabelOnMobile={showLabelOnMobile}
        />

        {/* Duration (只讀) */}
        <MetadataRow
          icon={Clock}
          label="Duration"
          value={duration}
          showLabelOnMobile={showLabelOnMobile}
        />

        <div className="meta-divider" />

        {/* Properties Section */}
        {/* Category 已移至標題下方的 Badge，這裡不再重複顯示 */}
        <MetadataRow
          icon={MapPin}
          label="Location"
          value={timeBox.location}
          inlineEdit={{
            // TimeBox 的 onLocationChange 仍為 string | undefined 介面，待統一後移除此轉換
            onSave: (value) => onLocationChange(value ?? undefined),
            placeholder: 'Location',
            maxLength: 100
          }}
          showLabelOnMobile={showLabelOnMobile}
        />

        <div className="meta-divider" />

        {/* Supplementary Info */}
        <div className="meta-row supplementary">
          <span className="meta-label">Created</span>
          <span className="meta-value">
            {timeBox.createdAt
              ? new Date(timeBox.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })
              : 'N/A'}
          </span>
        </div>
      </div>

      {/* Modals - Start Time Flow */}
      {modals.startDatePicker && (
        <SimpleDatePicker
          currentValue={startDateStr}
          onSelect={handleStartDateSelected}
          onClose={() => closeModal('startDatePicker')}
          hideNoDate={true}
          customQuickOptions={getTimeBoxQuickDateOptions()}
        />
      )}

      {modals.startTimePicker && (
        <TimePickerModal
          title="Select Start Time"
          currentValue={startTimeStr}
          onSelect={handleStartTimeSelected}
          onClose={() => closeModal('startTimePicker')}
          onBack={handleStartTimeBack}
          interval={15}
          use24Hours={false}
          pickerMode="wheel"
        />
      )}

      {/* Modals - End Time Flow */}
      {modals.endDatePicker && (
        <SimpleDatePicker
          currentValue={endDateStr}
          minDate={startDateStr}
          onSelect={handleEndDateSelected}
          onClose={() => closeModal('endDatePicker')}
          hideNoDate={true}
          customQuickOptions={getTimeBoxQuickDateOptions()}
        />
      )}

      {modals.endTimePicker && (
        <TimePickerModal
          title="Select End Time"
          currentValue={endTimeStr}
          minTime={getEndTimeMinTime()}
          onSelect={handleEndTimeSelected}
          onClose={() => closeModal('endTimePicker')}
          onBack={handleEndTimeBack}
          interval={15}
          use24Hours={false}
          pickerMode="wheel"
        />
      )}
    </div>
  );
};
